import { entityStore } from '~/utility/BinSentry-ui-utility';
import { BnModalMixin } from '../../common/bn-modal-mixin';
import { html, LitEntity, renderIf } from '../../utility/lit-entity';
import '../../common/bn-flash';

class MtechIntegrationModal extends BnModalMixin(LitEntity) {
  constructor() {
    super();
    this.id = 'mtech-integration-modal';
  }

  static get properties() {
    return {
      applyingInstallerCredentials: { type: Boolean, attribute: false },
    };
  }

  renderContents() {
    return html`
      <style>
        bn-modal#mtech-integration-modal bn-flash {
          padding: 0 10px;
        }
        bn-modal#mtech-integration-modal .description {
          padding: 0 20px 10px 20px;
        }
        bn-modal#mtech-integration-modal .status {
          font-weight: bold;
        }
        bn-modal#mtech-integration-modal .action-surface {
          position: relative;
        }
        bn-modal#mtech-integration-modal .hidden {
          visibility: hidden;
        }
        bn-modal#mtech-integration-modal .load-spinner {
          position: absolute;
          width: 100%;
        }
      </style>
      <h3 slot="title">${this.title}</h3>
      <div class="description">
        <p>
          The MTech Sonar integration allows forwarding bin level data to an MTech account.
        </p>
        <p>
          Credentials for an MTech Sonar installer account must be provided for the integration to work.
          Installer account credentials can be obtained from MTech.
        </p>
        <p>
          Once the installer account credentials are configured, add the following tags to bins in order to forward bin level data to MTech:
        </p>
        <ul>
          <li>
            mtechHouseId - unique id of the house where the bin is located within MTech Sonar environment
          </li>
          <li>
            mtechBinIndex - bin index within the house
          </li>
        </ul>
      </div>

      <bn-flash></bn-flash>

      <bn-surface>
        <div>
          <label>Status:</label>
          <span class="status">${this.entity.properties.installerCredentialsConfigured ? 'Enabled' : 'Disabled'}</span>
        </div>
      </bn-surface>

      ${renderIf(this.entity.hasAction('set-installer-credentials'), () => html`
        <bn-surface class="action-surface">
          ${renderIf(this.applyingInstallerCredentials, () => html`<div class="load-spinner"><bn-loading><p>Verifying installer credentials...</p></bn-loading></div>`)}
          <bn-action
            class="${this.applyingInstallerCredentials ? 'hidden' : ''}"
            .href="${this.href}"
            actionname="set-installer-credentials"
            @submit="${this._onInstallerCredentialsSubmit}"
            @response="${this._onInstallerCredentialsResponse}"
            @error="${this._onInstallerCredentialsError}"
            suppressflash
          >
            <h3 slot="header">
              ${this.entity.properties.installerCredentialsConfigured ? 'Update' : 'Set'} installer credentials
            </h3>
          </bn-action>
        </bn-surface>
      `)}

      ${renderIf(this.entity.hasAction('delete-installer-credentials'), () => html`
        <bn-surface>
          <bn-action
            .href="${this.href}"
            actionname="delete-installer-credentials"
            submittext="Delete installer credentials"
            buttonstyle='outlinedError'
            .preSubmit="${this._confirmDeletingInstallerCredentials}"
            @response="${() => this._onResponse('MTech integration has been disabled.')}"
            @error="${(e) => this._onError(e, 'Could not delete installer credentials.')}"
          >
            <h3 slot="header">Disable MTech integration</h3>
          </bn-action>
        </bn-surface>
      `)}
    `;
  }

  async _onInstallerCredentialsSubmit() {
    this.applyingInstallerCredentials = true;
  }

  async _onInstallerCredentialsResponse() {
    this.applyingInstallerCredentials = false;
    return this._onResponse('Installer credentials have been configured. MTech integration is enabled.');
  }

  async _onInstallerCredentialsError(e) {
    this.applyingInstallerCredentials = false;
    return this._onError(e, 'Could not set installer credentials.');
  }

  async _onResponse(successText) {
    this._getFlash().clear();
    await entityStore.fetchWait(this.getHref('self'));
    this._getFlash().success(successText);
  }

  async _onError(event, errorText) {
    this._getFlash().clear();
    this._getFlash().critical(html`${errorText}<div><code>${event.error.message}</code></div>`);
  }

  _getFlash() {
    return this.renderRoot.querySelector('bn-flash');
  }

  _confirmDeletingInstallerCredentials() {
    const confirmMessage = 'Are you sure you want to delete MTech installer credentials? This will disable MTech integration.';
    return window.confirm(confirmMessage);
  }
}

window.customElements.define('mtech-integration-modal', MtechIntegrationModal);
